import { HTTP_STATUS, isApiError, isSchemaValidationError } from '@brightlocal/api-client';

import { isSessionExpiredError } from 'shared/services/auth/sessionExpiredError';

import { isNetworkApiError } from './isNetworkApiError';
import { captureSchemaValidationError, captureSentryException, type Feature, type SentryLevel } from './sentry';

/**
 * A fully-resolved Sentry report for a query or mutation failure — what the default
 * policy produces and what a `resolve` override returns.
 */
export interface ErrorReport {
  /** Feature tag used to group and filter the error in Sentry. */
  feature: Feature;
  /** Sentry message. */
  message: string;
  /** Severity; `captureSentryException` defaults to `error` when omitted. */
  level?: SentryLevel;
  /** Tags merged into the Sentry scope (the default policy sets `httpStatus`). */
  tags?: Record<string, string>;
  /** Extra debugging context. */
  extra?: Record<string, unknown>;
  /** Sentry grouping override; see `captureSentryException`. */
  fingerprint?: string[];
}

/**
 * Error-reporting context for a query or mutation, consumed by the global
 * QueryCache / MutationCache handlers. Attach under `meta.errorReporting` to opt in
 * to Sentry reporting; omit to opt out. The static fields cover the common case;
 * `resolve` gives a single request full per-error control while still routing through
 * the shared pipeline.
 */
export interface ErrorReporting {
  /** Feature tag used to group and filter the error in Sentry. */
  feature: Feature;
  /** Short description of what failed — becomes the Sentry message. */
  errorMessage: string;
  /** API-client method name, reported when the failure is a response-schema mismatch. */
  method?: string;
  /**
   * Per-error override, run after the default policy computes its report — so a
   * request can treat specific statuses differently while still reporting. Receives
   * the caught error and the default report (`null` when the default policy would
   * skip it, e.g. a handled 401 or a network error). Return:
   *   - an `ErrorReport` to report exactly that (custom message/level/tags/extra),
   *   - `null` to skip reporting,
   *   - the passed `defaultReport` to keep the default.
   *
   * Network errors cannot be resurrected: `captureSentryException` drops them
   * regardless of what this returns, until the ADR-022 migration finishes and that
   * backstop can be removed.
   *
   * Not called for response-schema mismatches — those always go through
   * `captureSchemaValidationError` so their Zod issues are preserved.
   */
  resolve?: (error: unknown, defaultReport: ErrorReport | null) => ErrorReport | null;
}

/**
 * Shape of a query's or mutation's `meta`. Preserves TanStack's open record so
 * callers can attach arbitrary data (e.g. `placeId`), and adds a typed
 * `errorReporting` slot the global handlers read.
 */
export interface RequestMeta {
  [key: string]: unknown;
  errorReporting?: ErrorReporting;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: RequestMeta;
    mutationMeta: RequestMeta;
  }
}

/**
 * The report the default policy sends for a given error, or `null` when it should
 * be dropped. Folds the HTTP status into the message and an `httpStatus` tag so
 * 4xx/5xx stay distinguishable in Sentry instead of collapsing into one issue.
 */
const buildDefaultReport = (error: unknown, feature: Feature, errorMessage: string): ErrorReport | null => {
  if (!isApiError(error)) {
    return { feature, message: errorMessage };
  }

  // Nothing actionable: connectivity failures and aborts are client-side (and the
  // `networkError` factory defaults their status to 500, so they would otherwise look
  // like real server errors), and a handled 401 means the auth layer has already
  // logged the user out and redirected to login.
  if (isNetworkApiError(error) || error.status === HTTP_STATUS.AUTHORIZATION_ERROR || isSessionExpiredError(error)) {
    return null;
  }

  const { status } = error;

  if (status === undefined) {
    return { feature, message: errorMessage };
  }

  return {
    feature,
    message: `${errorMessage} (${status})`,
    tags: { httpStatus: String(status) }
  };
};

/**
 * Central Sentry reporting for query and mutation failures, wired once into the
 * QueryClient's `QueryCache` and `MutationCache`. Fires once per settled error
 * (after retries), replacing the per-`queryFn` try/catch that TanStack v5 forced
 * after removing per-query `onError`.
 *
 * Applies the default policy (drop handled 401s, route schema mismatches, fold the
 * HTTP status into the message/tag), then lets the caller's `resolve` override the
 * outcome for any status while still going through this one code path.
 *
 * @param error - Error thrown by the query or mutation function.
 * @param meta - The failing request's `meta`; reporting is skipped unless `errorReporting` is present.
 */
export const reportRequestError = (error: unknown, meta: RequestMeta | undefined): void => {
  const config = meta?.errorReporting;

  if (config === undefined) return;

  const { feature, errorMessage, method, resolve } = config;

  // Response-schema mismatches keep their dedicated channel (preserves Zod issues).
  if (isApiError(error) && isSchemaValidationError(error)) {
    captureSchemaValidationError({ feature, error, method: method ?? 'unknown' });
    return;
  }

  const defaultReport = buildDefaultReport(error, feature, errorMessage);
  const report = resolve ? resolve(error, defaultReport) : defaultReport;

  if (report === null) return;

  captureSentryException({ ...report, error });
};
